import React, { useState, useEffect, useRef } from 'react';
import { useToast } from './Toast';
import firebaseAuthService from '../services/firebase/FirebaseAuthService';
import { db, storage } from '../config/firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../hooks/useConfirm';

interface ProviderProfile {
  // Personal Information
  firstName: string;
  lastName: string;
  middleName?: string;
  preferredName?: string;
  dateOfBirth?: string;
  gender?: string;
  email: string;
  phone: string;
  mobilePhone?: string;

  // Professional Credentials
  title: string;
  specialty?: string;
  taxonomy?: string;
  npi?: string;
  licenses: {
    licenseNumber: string;
    state: string;
    expirationDate: string;
    type: string;
  }[];

  // Account Settings
  password?: string;
  twoFactorEnabled: boolean;
  notificationPreferences: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };

  // Professional Profile (Client-Facing)
  bio?: string;
  profilePhoto?: string;
  introductionVideo?: string;
  yearsOfExperience?: number;
  educationBackground?: string;
  specializations?: string[];
  languages?: string[];
}

const ProfileSettings: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'personal' | 'credentials' | 'account' | 'professional'>('personal');
  const [profile, setProfile] = useState<ProviderProfile>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    licenses: [],
    twoFactorEnabled: false,
    notificationPreferences: {
      email: true,
      sms: false,
      push: true
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // License management
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [newLicense, setNewLicense] = useState({ licenseNumber: '', state: '', expirationDate: '', type: 'LPC' });

  const handleAddLicense = () => {
    if (!newLicense.licenseNumber || !newLicense.state || !newLicense.expirationDate) {
      toast.warning('Missing Fields', 'Please fill in all license fields.');
      return;
    }
    const updated = [...profile.licenses, { ...newLicense }];
    updateProfile({ licenses: updated });
    setNewLicense({ licenseNumber: '', state: '', expirationDate: '', type: 'LPC' });
    setShowLicenseForm(false);
    toast.success('License Added', 'Your license has been added successfully.');
  };

  const handleRemoveLicense = (index: number) => {
    const updated = profile.licenses.filter((_, i) => i !== index);
    updateProfile({ licenses: updated });
    toast.success('License Removed', 'License has been removed.');
  };

  // File upload refs
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const { user } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Invalid File', 'Please upload a JPG or PNG image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Too Large', 'Photo must be under 5 MB.');
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const storageRef = ref(storage, `providers/${user.uid}/profile-photo`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      updateProfile({ profilePhoto: url });
      // Save immediately to Firestore
      await updateDoc(doc(db, 'providers', user.uid), { profilePhoto: url });
      toast.success('Photo Uploaded', 'Your profile photo has been updated.');
    } catch (error: any) {
      toast.error('Upload Failed', error.message || 'Failed to upload photo.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Invalid File', 'Please upload a video file (MP4, WebM, etc.).');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Too Large', 'Video must be under 50 MB.');
      return;
    }

    setIsUploadingVideo(true);
    try {
      const storageRef = ref(storage, `providers/${user.uid}/intro-video`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      updateProfile({ introductionVideo: url });
      // Save immediately to Firestore
      await updateDoc(doc(db, 'providers', user.uid), { introductionVideo: url });
      toast.success('Video Uploaded', 'Your introduction video has been updated.');
    } catch (error: any) {
      toast.error('Upload Failed', error.message || 'Failed to upload video.');
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.warning('Missing Fields', 'Please fill in all password fields.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Mismatch', 'New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Too Short', 'New password must be at least 8 characters.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await firebaseAuthService.changePassword(currentPassword, newPassword);
      toast.success('Password Changed', 'Your password has been updated successfully.');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to change password.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  useEffect(() => {
    if (!user) { setIsLoading(false); return; }

    const loadProfile = async () => {
      try {
        // Load from Firestore providers/{uid}
        const providerDoc = await getDoc(doc(db, 'providers', user.uid));
        const userData = await getDoc(doc(db, 'users', user.uid));

        const providerData = providerDoc.exists() ? providerDoc.data() : {};
        const userD = userData.exists() ? userData.data() : {};

        setProfile(prev => ({
          ...prev,
          firstName: providerData.firstName || userD.displayName?.split(' ')[0] || user.displayName?.split(' ')[0] || '',
          lastName: providerData.lastName || userD.displayName?.split(' ').slice(1).join(' ') || user.displayName?.split(' ').slice(1).join(' ') || '',
          email: providerData.email || user.email || '',
          phone: providerData.phone || userD.phoneNumber || user.phoneNumber || '',
          mobilePhone: providerData.mobilePhone || '',
          title: providerData.title || (providerData.licenseType === 'MD' ? 'Dr.' : ''),
          middleName: providerData.middleName || '',
          preferredName: providerData.preferredName || '',
          dateOfBirth: providerData.dateOfBirth || '',
          gender: providerData.gender || '',
          specialty: providerData.specialty || (providerData.specialties?.join(', ')) || '',
          npi: providerData.npiNumber || providerData.npi || '',
          taxonomy: providerData.taxonomy || '',
          licenses: providerData.licenses || (providerData.licenseNumber ? [{
            licenseNumber: providerData.licenseNumber,
            state: providerData.stateOfLicensure || '',
            expirationDate: '',
            type: providerData.licenseType || 'LPC',
          }] : []),
          bio: providerData.bio || '',
          profilePhoto: providerData.profilePhoto || '',
          introductionVideo: providerData.introductionVideo || '',
          yearsOfExperience: providerData.yearsOfExperience,
          educationBackground: providerData.educationBackground || '',
          specializations: providerData.specializations || providerData.specialties || [],
          languages: providerData.languages || [],
          twoFactorEnabled: providerData.twoFactorEnabled || false,
          notificationPreferences: providerData.notificationPreferences || {
            email: true, sms: false, push: true,
          },
        }));
      } catch (error) {
        console.error('Error loading provider profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    try {
      const providerRef = doc(db, 'providers', user.uid);
      const providerDoc = await getDoc(providerRef);

      const updates: Record<string, any> = {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        phone: profile.phone,
        mobilePhone: profile.mobilePhone || null,
        title: profile.title,
        middleName: profile.middleName || null,
        preferredName: profile.preferredName || null,
        dateOfBirth: profile.dateOfBirth || null,
        gender: profile.gender || null,
        specialty: profile.specialty || null,
        npi: profile.npi || null,
        taxonomy: profile.taxonomy || null,
        licenses: profile.licenses,
        bio: profile.bio || null,
        profilePhoto: profile.profilePhoto || null,
        introductionVideo: profile.introductionVideo || null,
        yearsOfExperience: profile.yearsOfExperience || null,
        educationBackground: profile.educationBackground || null,
        specializations: profile.specializations || [],
        languages: profile.languages || [],
        twoFactorEnabled: profile.twoFactorEnabled,
        notificationPreferences: profile.notificationPreferences,
      };

      if (providerDoc.exists()) {
        await updateDoc(providerRef, updates);
      } else {
        await setDoc(providerRef, { uid: user.uid, ...updates });
      }

      // Also update displayName in users/{uid}
      const displayName = `${profile.firstName} ${profile.lastName}`.trim();
      if (displayName) {
        await updateDoc(doc(db, 'users', user.uid), { displayName });
      }

      setHasChanges(false);
      window.dispatchEvent(new CustomEvent('providerProfileUpdated'));
      toast.success('Profile Updated', 'Your profile has been saved successfully.');
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast.error('Save Failed', error.message || 'Failed to save profile.');
    }
  };

  const handleCancel = async () => {
    if (hasChanges) {
      const confirmed = await confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to cancel?',
        confirmLabel: 'Discard',
        variant: 'warning'
      });
      if (!confirmed) return;
    }
    onClose();
  };

  const updateProfile = (updates: Partial<ProviderProfile>) => {
    setProfile(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: '#0A2540',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #6CA6C1'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #6CA6C1',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 600,
            color: '#FFFFFF',
            margin: 0
          }}>My Profile</h2>
          <button
            onClick={handleCancel}
            style={{
              background: 'none',
              border: 'none',
              color: '#FFFFFF',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0 8px'
            }}
          >×</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '16px 24px',
          borderBottom: '1px solid #6CA6C1'
        }}>
          {[
            { key: 'personal', label: 'Personal Info' },
            { key: 'credentials', label: 'Credentials' },
            { key: 'account', label: 'Account Settings' },
            { key: 'professional', label: 'Professional Profile' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: activeTab === tab.key ? '#6CA6C1' : 'transparent',
                color: '#FFFFFF',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.key ? 600 : 400
              }}
            >{tab.label}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
              Loading profile...
            </div>
          )}
          {!isLoading && activeTab === 'personal' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>Personal Information</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Title
                  </label>
                  <select
                    value={profile.title}
                    onChange={(e) => updateProfile({ title: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  >
                    <option value="Dr.">Dr.</option>
                    <option value="Mr.">Mr.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Mrs.">Mrs.</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={profile.firstName}
                    onChange={(e) => updateProfile({ firstName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Middle Name
                  </label>
                  <input
                    type="text"
                    value={profile.middleName || ''}
                    onChange={(e) => updateProfile({ middleName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={profile.lastName}
                    onChange={(e) => updateProfile({ lastName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Preferred Name
                  </label>
                  <input
                    type="text"
                    value={profile.preferredName || ''}
                    onChange={(e) => updateProfile({ preferredName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={profile.dateOfBirth || ''}
                    onChange={(e) => updateProfile({ dateOfBirth: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Gender
                  </label>
                  <select
                    value={profile.gender || ''}
                    onChange={(e) => updateProfile({ gender: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Non-binary">Non-binary</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginTop: '16px', marginBottom: '8px' }}>Contact Information</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => updateProfile({ email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => updateProfile({ phone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Mobile Phone
                  </label>
                  <input
                    type="tel"
                    value={profile.mobilePhone || ''}
                    onChange={(e) => updateProfile({ mobilePhone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {!isLoading && activeTab === 'credentials' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>Professional Credentials</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Specialty
                  </label>
                  <input
                    type="text"
                    value={profile.specialty || ''}
                    onChange={(e) => updateProfile({ specialty: e.target.value })}
                    placeholder="e.g., Clinical Psychology"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Taxonomy Code
                  </label>
                  <input
                    type="text"
                    value={profile.taxonomy || ''}
                    onChange={(e) => updateProfile({ taxonomy: e.target.value })}
                    placeholder="e.g., 103T00000X"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    NPI Number
                  </label>
                  <input
                    type="text"
                    value={profile.npi || ''}
                    onChange={(e) => updateProfile({ npi: e.target.value })}
                    placeholder="10-digit NPI"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginTop: '16px', marginBottom: '8px' }}>Licenses</h3>

              <div style={{
                padding: '16px',
                backgroundColor: '#113F67',
                borderRadius: '6px',
                border: '1px solid #6CA6C1'
              }}>
                {profile.licenses.length === 0 ? (
                  <p style={{ color: '#87CEEB', fontSize: '14px', margin: 0 }}>
                    No licenses added yet. Click "Add License" to get started.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {profile.licenses.map((license, index) => (
                      <div key={index} style={{
                        padding: '12px',
                        backgroundColor: '#0A2540',
                        borderRadius: '6px',
                        border: '1px solid #6CA6C1',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', flex: 1 }}>
                          <div>
                            <span style={{ fontSize: '12px', color: '#87CEEB' }}>License Number</span>
                            <p style={{ margin: '4px 0 0 0', color: '#FFFFFF', fontSize: '14px' }}>{license.licenseNumber}</p>
                          </div>
                          <div>
                            <span style={{ fontSize: '12px', color: '#87CEEB' }}>State</span>
                            <p style={{ margin: '4px 0 0 0', color: '#FFFFFF', fontSize: '14px' }}>{license.state}</p>
                          </div>
                          <div>
                            <span style={{ fontSize: '12px', color: '#87CEEB' }}>Expires</span>
                            <p style={{ margin: '4px 0 0 0', color: '#FFFFFF', fontSize: '14px' }}>{license.expirationDate}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveLicense(index)}
                          style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '18px', padding: '4px 8px' }}
                          aria-label="Remove license"
                        >x</button>
                      </div>
                    ))}
                  </div>
                )}

                {showLicenseForm && (
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', backgroundColor: '#0A2540', borderRadius: '6px', border: '1px solid #6CA6C1' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label htmlFor="lic-type" style={{ display: 'block', fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>Type</label>
                        <select
                          id="lic-type"
                          value={newLicense.type}
                          onChange={(e) => setNewLicense({ ...newLicense, type: e.target.value })}
                          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #6CA6C1', backgroundColor: '#113F67', color: '#fff', fontSize: '14px' }}
                        >
                          <option value="LPC">LPC</option>
                          <option value="LCSW">LCSW</option>
                          <option value="LMFT">LMFT</option>
                          <option value="LPCC">LPCC</option>
                          <option value="PsyD">PsyD</option>
                          <option value="PhD">PhD</option>
                          <option value="LMHC">LMHC</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="lic-number" style={{ display: 'block', fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>License Number</label>
                        <input
                          id="lic-number"
                          value={newLicense.licenseNumber}
                          onChange={(e) => setNewLicense({ ...newLicense, licenseNumber: e.target.value })}
                          placeholder="e.g., LPC-12345"
                          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #6CA6C1', backgroundColor: '#113F67', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label htmlFor="lic-state" style={{ display: 'block', fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>State</label>
                        <input
                          id="lic-state"
                          value={newLicense.state}
                          onChange={(e) => setNewLicense({ ...newLicense, state: e.target.value })}
                          placeholder="e.g., TX"
                          maxLength={2}
                          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #6CA6C1', backgroundColor: '#113F67', color: '#fff', fontSize: '14px', boxSizing: 'border-box', textTransform: 'uppercase' }}
                        />
                      </div>
                      <div>
                        <label htmlFor="lic-exp" style={{ display: 'block', fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>Expiration Date</label>
                        <input
                          id="lic-exp"
                          type="date"
                          value={newLicense.expirationDate}
                          onChange={(e) => setNewLicense({ ...newLicense, expirationDate: e.target.value })}
                          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #6CA6C1', backgroundColor: '#113F67', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={handleAddLicense} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#D4A017', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Save License</button>
                      <button onClick={() => setShowLicenseForm(false)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #6CA6C1', backgroundColor: 'transparent', color: '#6CA6C1', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                    </div>
                  </div>
                )}

                {!showLicenseForm && (
                  <button
                    onClick={() => setShowLicenseForm(true)}
                    style={{
                      marginTop: '12px',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: 'transparent',
                      color: '#6CA6C1',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >+ Add License</button>
                )}
              </div>
            </div>
          )}

          {!isLoading && activeTab === 'account' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px' }}>Security Settings</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#113F67',
                    borderRadius: '6px',
                    border: '1px solid #6CA6C1',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 4px 0' }}>Change Password</h4>
                        <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>Update your password to keep your account secure</p>
                      </div>
                      <button
                        onClick={() => setShowPasswordForm(!showPasswordForm)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: '1px solid #6CA6C1',
                          backgroundColor: 'transparent',
                          color: '#6CA6C1',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >{showPasswordForm ? 'Cancel' : 'Change'}</button>
                    </div>

                    {showPasswordForm && (
                      <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label htmlFor="current-password" style={{ display: 'block', fontSize: '13px', color: '#87CEEB', marginBottom: '4px' }}>Current Password</label>
                          <input
                            id="current-password"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter current password"
                            style={{
                              width: '100%', padding: '10px 12px', borderRadius: '6px',
                              border: '1px solid #6CA6C1', backgroundColor: '#0A2A43',
                              color: '#fff', fontSize: '14px', boxSizing: 'border-box'
                            }}
                          />
                        </div>
                        <div>
                          <label htmlFor="new-password" style={{ display: 'block', fontSize: '13px', color: '#87CEEB', marginBottom: '4px' }}>New Password</label>
                          <input
                            id="new-password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="At least 8 characters"
                            style={{
                              width: '100%', padding: '10px 12px', borderRadius: '6px',
                              border: '1px solid #6CA6C1', backgroundColor: '#0A2A43',
                              color: '#fff', fontSize: '14px', boxSizing: 'border-box'
                            }}
                          />
                        </div>
                        <div>
                          <label htmlFor="confirm-new-password" style={{ display: 'block', fontSize: '13px', color: '#87CEEB', marginBottom: '4px' }}>Confirm New Password</label>
                          <input
                            id="confirm-new-password"
                            type="password"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            placeholder="Confirm new password"
                            style={{
                              width: '100%', padding: '10px 12px', borderRadius: '6px',
                              border: '1px solid #6CA6C1', backgroundColor: '#0A2A43',
                              color: '#fff', fontSize: '14px', boxSizing: 'border-box'
                            }}
                          />
                        </div>
                        <button
                          onClick={handleChangePassword}
                          disabled={isChangingPassword}
                          style={{
                            padding: '10px 20px', borderRadius: '6px', border: 'none',
                            backgroundColor: '#D4A017', color: '#fff', cursor: isChangingPassword ? 'not-allowed' : 'pointer',
                            fontSize: '14px', fontWeight: 600, opacity: isChangingPassword ? 0.7 : 1,
                            alignSelf: 'flex-start'
                          }}
                        >
                          {isChangingPassword ? 'Updating...' : 'Update Password'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#113F67',
                    borderRadius: '6px',
                    border: '1px solid #6CA6C1',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 4px 0' }}>Two-Factor Authentication</h4>
                      <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
                        {profile.twoFactorEnabled ? 'Enabled - Your account is protected' : 'Not enabled - Enhance your account security'}
                      </p>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={profile.twoFactorEnabled}
                        onChange={(e) => updateProfile({ twoFactorEnabled: e.target.checked })}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                        {profile.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px' }}>Notification Preferences</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#113F67',
                    borderRadius: '6px',
                    border: '1px solid #6CA6C1',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Email Notifications</span>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={profile.notificationPreferences.email}
                        onChange={(e) => updateProfile({
                          notificationPreferences: {
                            ...profile.notificationPreferences,
                            email: e.target.checked
                          }
                        })}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                        {profile.notificationPreferences.email ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#113F67',
                    borderRadius: '6px',
                    border: '1px solid #6CA6C1',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '14px', color: '#FFFFFF' }}>SMS Notifications</span>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={profile.notificationPreferences.sms}
                        onChange={(e) => updateProfile({
                          notificationPreferences: {
                            ...profile.notificationPreferences,
                            sms: e.target.checked
                          }
                        })}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                        {profile.notificationPreferences.sms ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#113F67',
                    borderRadius: '6px',
                    border: '1px solid #6CA6C1',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Push Notifications</span>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={profile.notificationPreferences.push}
                        onChange={(e) => updateProfile({
                          notificationPreferences: {
                            ...profile.notificationPreferences,
                            push: e.target.checked
                          }
                        })}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                        {profile.notificationPreferences.push ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isLoading && activeTab === 'professional' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>Professional Profile (Client-Facing)</h3>
              <p style={{ fontSize: '14px', color: '#87CEEB', marginTop: '-12px' }}>
                This information will be visible to your clients
              </p>

              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                  Profile Photo
                </label>
                <div style={{
                  padding: '24px',
                  backgroundColor: '#113F67',
                  borderRadius: '6px',
                  border: '1px dashed #6CA6C1',
                  textAlign: 'center'
                }}>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  {profile.profilePhoto ? (
                    <div>
                      <img src={profile.profilePhoto} alt="Profile" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                      <div style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => photoInputRef.current?.click()}
                          style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #6CA6C1', backgroundColor: 'transparent', color: '#6CA6C1', cursor: 'pointer', fontSize: '14px' }}
                        >Change Photo</button>
                        <button
                          onClick={() => updateProfile({ profilePhoto: undefined })}
                          style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #EF4444', backgroundColor: 'transparent', color: '#EF4444', cursor: 'pointer', fontSize: '14px' }}
                        >Remove</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p style={{ color: '#87CEEB', fontSize: '14px', margin: '0 0 4px 0' }}>
                        {isUploadingPhoto ? 'Uploading...' : 'No photo uploaded'}
                      </p>
                      <p style={{ color: '#87CEEB', fontSize: '12px', margin: '0 0 12px 0' }}>
                        JPG, PNG, or WebP under 5 MB
                      </p>
                      <button
                        onClick={() => photoInputRef.current?.click()}
                        disabled={isUploadingPhoto}
                        style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #6CA6C1', backgroundColor: 'transparent', color: '#6CA6C1', cursor: isUploadingPhoto ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: isUploadingPhoto ? 0.7 : 1 }}
                      >{isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}</button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                  Introduction Video
                </label>
                <div style={{
                  padding: '24px',
                  backgroundColor: '#113F67',
                  borderRadius: '6px',
                  border: '1px dashed #6CA6C1',
                  textAlign: 'center'
                }}>
                  {profile.introductionVideo ? (
                    <div>
                      <video
                        src={profile.introductionVideo}
                        controls
                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                      />
                      <button
                        onClick={() => updateProfile({ introductionVideo: undefined })}
                        style={{
                          marginTop: '12px',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: '1px solid #6CA6C1',
                          backgroundColor: 'transparent',
                          color: '#6CA6C1',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >Remove Video</button>
                    </div>
                  ) : (
                    <div>
                      <p style={{ color: '#87CEEB', fontSize: '14px', margin: '0 0 12px 0' }}>
                        No video uploaded
                      </p>
                      <p style={{ color: '#87CEEB', fontSize: '12px', margin: '0 0 12px 0' }}>
                        Upload a video file (MP4, WebM, MOV) under 50 MB.
                      </p>
                      <input
                        type="file"
                        ref={videoInputRef}
                        accept="video/mp4,video/webm,video/quicktime"
                        onChange={handleVideoUpload}
                        style={{ display: 'none' }}
                      />
                      <button
                        onClick={() => videoInputRef.current?.click()}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: '1px solid #6CA6C1',
                          backgroundColor: 'transparent',
                          color: '#6CA6C1',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >Choose introduction video</button>
                      <p style={{ color: '#87CEEB', fontSize: '12px', margin: '12px 0 0 0', fontStyle: 'italic' }}>
                        Accepted formats: MP4, WebM, MOV
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                  Professional Bio
                </label>
                <textarea
                  value={profile.bio || ''}
                  onChange={(e) => updateProfile({ bio: e.target.value })}
                  placeholder="Tell your clients about your background, approach, and expertise..."
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #6CA6C1',
                    backgroundColor: '#113F67',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Years of Experience
                  </label>
                  <input
                    type="number"
                    value={profile.yearsOfExperience || ''}
                    onChange={(e) => updateProfile({ yearsOfExperience: parseInt(e.target.value) || undefined })}
                    placeholder="e.g., 10"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                    Education Background
                  </label>
                  <input
                    type="text"
                    value={profile.educationBackground || ''}
                    onChange={(e) => updateProfile({ educationBackground: e.target.value })}
                    placeholder="e.g., PhD in Clinical Psychology, Harvard"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #6CA6C1',
                      backgroundColor: '#113F67',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                  Specializations <span style={{ color: '#9ca3af', fontWeight: 400 }}>(select all that apply)</span>
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {['Anxiety', 'Depression', 'Trauma / PTSD', 'Substance Abuse', 'Couples Therapy',
                    'Family Therapy', 'Child & Adolescent', 'Eating Disorders', 'OCD', 'ADHD',
                    'Grief & Loss', 'Anger Management', 'Career Counseling', 'Chronic Pain',
                    'Sleep Disorders', 'Bipolar Disorder', 'Personality Disorders', 'Self-Harm',
                    'Women\'s Issues', 'Men\'s Issues', 'LGBTQ+', 'Geriatric'].map((spec) => (
                    <label key={spec} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#FFFFFF', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={profile.specializations?.includes(spec) || false}
                        onChange={() => {
                          const current = profile.specializations || [];
                          updateProfile({
                            specializations: current.includes(spec)
                              ? current.filter(s => s !== spec)
                              : [...current, spec]
                          });
                        }}
                        style={{ accentColor: '#D4A017', width: '16px', height: '16px', flexShrink: 0 }}
                      />
                      {spec}
                    </label>
                  ))}
                </div>
                {(profile.specializations?.length || 0) > 0 && (
                  <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '4px' }}>
                    {profile.specializations!.length} selected
                  </p>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#FFFFFF', marginBottom: '8px' }}>
                  Languages Spoken <span style={{ color: '#9ca3af', fontWeight: 400 }}>(select all that apply)</span>
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67',
                }}>
                  {['English', 'Spanish', 'French', 'Mandarin', 'Cantonese', 'Japanese',
                    'Korean', 'Vietnamese', 'Arabic', 'Hindi', 'Portuguese', 'German',
                    'Italian', 'Russian', 'Tagalog', 'ASL (American Sign Language)'].map((lang) => (
                    <label key={lang} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#FFFFFF', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={profile.languages?.includes(lang) || false}
                        onChange={() => {
                          const current = profile.languages || [];
                          updateProfile({
                            languages: current.includes(lang)
                              ? current.filter(l => l !== lang)
                              : [...current, lang]
                          });
                        }}
                        style={{ accentColor: '#D4A017', width: '16px', height: '16px', flexShrink: 0 }}
                      />
                      {lang}
                    </label>
                  ))}
                </div>
                {(profile.languages?.length || 0) > 0 && (
                  <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '4px' }}>
                    {profile.languages!.length} selected
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #6CA6C1',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '10px 24px',
              borderRadius: '6px',
              border: '1px solid #6CA6C1',
              backgroundColor: 'transparent',
              color: '#FFFFFF',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500
            }}
          >Cancel</button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            style={{
              padding: '10px 24px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: hasChanges ? '#D4A017' : '#113F67',
              color: '#FFFFFF',
              cursor: hasChanges ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: 600,
              opacity: hasChanges ? 1 : 0.5,
              transition: 'all 0.2s'
            }}
          >Save Changes</button>
        </div>
      </div>
      {ConfirmDialog}
    </div>
  );
};

export default ProfileSettings;
